import { summaryTypeKeys } from '../utils/skillSummary'
import Icon from '../components/Icon'
import { useIsMobile } from '../hooks/useIsMobile'
import styles from './TeamMemberRow.module.css'

const AVATAR_PALETTE = [
  '#E05A4E', '#E08A4E', '#E0C44E', '#6DBE6D',
  '#4E9EE0', '#7B6DE0', '#C44EBE', '#4EC4C4',
]

function avatarColor(id) {
  const seed = String(id)
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]
}

// The three stats lines (teams, skill counts, case metrics) are identical
// between the desktop row and the mobile card — shared so neither layout
// drifts from the other.
function MemberStats({ member, summary }) {
  return (
    <>
      <div className={styles.statsLine}>
        <span className={styles.statsIcon}><Icon name="pin" /></span>
        {member.teams.length === 0
          ? <span className={styles.statsMuted}>Unassigned</span>
          : member.teams.map(t => (
              <span
                key={t.id}
                className={`${styles.teamChip} ${t.is_primary ? styles.teamChipPrimary : ''}`}
              >
                {t.name}
              </span>
            ))
        }
      </div>
      <div className={styles.statsLine}>
        <span className={styles.statsIcon}><Icon name="bars-stacked" /></span>
        <span className={styles.statsText}>{summary.Total ?? '0'} total skills</span>
        {summaryTypeKeys(summary).map(k => (
          <span key={k} className={`${styles.pill} ${styles[`pill${k}`]}`}>
            {summary[k]} {k}
          </span>
        ))}
      </div>
      <div className={styles.statsLine}>
        <span className={styles.statsIcon}><Icon name="cases" /></span>
        <span className={styles.statsText}>{member.cases?.resolved ?? 0} Cases resolved</span>
        <span className={styles.dot}>•</span>
        <span className={styles.statsText}>{member.cases?.assisted ?? 0} Cases assisted</span>
        <span className={styles.dot}>•</span>
        <span className={styles.statsText}>{member.cases?.avg_resolution ?? '0h'} avg. resolution</span>
        <span className={styles.dot}>•</span>
        <span className={styles.statsText}>{member.cases?.first_response ?? '0m'} First response</span>
      </div>
    </>
  )
}

function NewSkillsBadge({ member }) {
  if (member.new_skills <= 0) return null
  return (
    <div className={styles.newSkillsBadge}>
      <span className={styles.pinIcon}><Icon name="pin" /></span>
      <span>{member.new_skills} new skill{member.new_skills !== 1 ? 's' : ''}</span>
    </div>
  )
}

/**
 * TeamMemberRow — renders a single team member entry in the Team page list.
 * The whole row/card is the actionable surface — clicking anywhere opens the
 * agent's skills modal (the parent derives review vs details mode).
 *
 * Props:
 *   member {object} — team member data shape from src/api/team.js
 *   onOpen {function} — called when the row/card is activated (click or Enter/Space)
 */
export default function TeamMemberRow({ member, onOpen }) {
  const summary = member.skills_summary ?? {}
  const isMobile = useIsMobile()

  // Only a click on the surface itself opens the modal, so inner controls that
  // grow their own behavior later won't also trigger it.
  const activate = e => {
    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      onOpen(e)
    }
  }

  // Mobile: the desktop row's four side-by-side columns (avatar, identity,
  // stats, actions) don't have room to sit in one line on a phone — a
  // compact header (avatar + name) with the stats block stacked below fits
  // instead of squeezing everything into 375px.
  if (isMobile) {
    return (
      <div
        className={`${styles.mobileCard} ${styles.clickable}`}
        onClick={onOpen}
        onKeyDown={activate}
        role="button"
        tabIndex={0}
      >
        <div className={styles.mobileHeader}>
          <div className={styles.avatar} style={{ backgroundColor: avatarColor(member.id) }}>
            {member.initials}
          </div>
          <div className={styles.identity}>
            <span className={styles.name}>{member.name}</span>
            <span className={styles.role}>{member.role}</span>
          </div>
        </div>

        <NewSkillsBadge member={member} />

        <div className={styles.mobileStats}>
          <MemberStats member={member} summary={summary} />
        </div>
      </div>
    )
  }

  return (
    <div
      className={`${styles.row} ${styles.clickable}`}
      onClick={onOpen}
      onKeyDown={activate}
      role="button"
      tabIndex={0}
    >

      <div className={styles.avatar} style={{ backgroundColor: avatarColor(member.id) }}>
        {member.initials}
      </div>

      <div className={styles.identity}>
        <span className={styles.name}>{member.name}</span>
        <span className={styles.role}>{member.role}</span>
      </div>

      <div className={styles.stats}>
        <MemberStats member={member} summary={summary} />
      </div>

      <div className={styles.actions}>
        <NewSkillsBadge member={member} />
      </div>

    </div>
  )
}
